import type { ReactElement } from 'react';
import { Refine, Authenticated } from '@refinedev/core';
import { ThemedLayoutV2, ErrorComponent } from '@refinedev/antd';
import routerProvider, { NavigateToResource } from '@refinedev/react-router';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router';
import {
  DashboardOutlined,
  ShoppingCartOutlined,
  NodeIndexOutlined,
  PictureOutlined,
  TeamOutlined,
  ShoppingOutlined,
  AuditOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { App as AntdApp, ConfigProvider } from 'antd';

import { authProvider } from '@/providers/authProvider';
import { dataProvider } from '@/providers/dataProvider';
import { LoginPage } from '@/pages/login/LoginPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { OrderList } from '@/pages/orders/OrderList';
import { OrderShow } from '@/pages/orders/OrderShow';
import { CheckpointList } from '@/pages/checkpoints/CheckpointList';
import { CheckpointShow } from '@/pages/checkpoints/CheckpointShow';
import { ExampleList } from '@/pages/examples/ExampleList';
import { CustomerList } from '@/pages/customers/CustomerList';
import { CustomerShow } from '@/pages/customers/CustomerShow';
import { CustomerEdit } from '@/pages/customers/CustomerEdit';
import { CustomerOrderList } from '@/pages/customerOrders/CustomerOrderList';
import { CustomerOrderShow } from '@/pages/customerOrders/CustomerOrderShow';
import { ConsentLogList } from '@/pages/consentLogs/ConsentLogList';
import { ConsentLogShow } from '@/pages/consentLogs/ConsentLogShow';
import { RecoveryEventList } from '@/pages/recoveryEvents/RecoveryEventList';
import { RecoveryEventShow } from '@/pages/recoveryEvents/RecoveryEventShow';

import '@refinedev/antd/dist/reset.css';

export function App(): ReactElement {
  return (
    <BrowserRouter>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#6366f1',
          },
        }}
      >
        <AntdApp>
          <Refine
            dataProvider={dataProvider}
            routerProvider={routerProvider}
            authProvider={authProvider}
            resources={[
              {
                name: 'dashboard',
                list: '/',
                meta: {
                  label: 'Dashboard',
                  icon: <DashboardOutlined />,
                },
              },
              {
                name: 'orders',
                list: '/orders',
                show: '/orders/show/:id',
                meta: {
                  label: 'Orders',
                  icon: <ShoppingCartOutlined />,
                },
              },
              {
                name: 'checkpoints',
                list: '/checkpoints',
                show: '/checkpoints/show/:id',
                meta: {
                  label: 'Checkpoints',
                  icon: <NodeIndexOutlined />,
                },
              },
              {
                name: 'examples',
                list: '/examples',
                meta: {
                  label: 'Examples',
                  icon: <PictureOutlined />,
                },
              },
              {
                name: 'customers',
                list: '/customers',
                show: '/customers/show/:id',
                edit: '/customers/edit/:id',
                meta: {
                  label: 'Customers',
                  icon: <TeamOutlined />,
                },
              },
              {
                name: 'customer-orders',
                list: '/customer-orders',
                show: '/customer-orders/show/:id',
                meta: {
                  label: 'Customer Orders',
                  icon: <ShoppingOutlined />,
                },
              },
              {
                name: 'consent-logs',
                list: '/consent-logs',
                show: '/consent-logs/show/:id',
                meta: {
                  label: 'Consent Logs',
                  icon: <AuditOutlined />,
                },
              },
              {
                name: 'recovery-events',
                list: '/recovery-events',
                show: '/recovery-events/show/:id',
                meta: {
                  label: 'Recovery Events',
                  icon: <HistoryOutlined />,
                },
              },
            ]}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
            }}
          >
            <Routes>
              <Route
                element={
                  <Authenticated key="auth-pages" fallback={<Outlet />}>
                    <NavigateToResource resource="dashboard" />
                  </Authenticated>
                }
              >
                <Route path="/login" element={<LoginPage />} />
              </Route>

              <Route
                element={
                  <Authenticated
                    key="authenticated-routes"
                    redirectOnFail="/login"
                  >
                    <ThemedLayoutV2 Title={() => <span>Woolaby Admin</span>}>
                      <Outlet />
                    </ThemedLayoutV2>
                  </Authenticated>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="/orders">
                  <Route index element={<OrderList />} />
                  <Route path="show/:id" element={<OrderShow />} />
                </Route>
                <Route path="/checkpoints">
                  <Route index element={<CheckpointList />} />
                  <Route path="show/:id" element={<CheckpointShow />} />
                </Route>
                <Route path="/examples">
                  <Route index element={<ExampleList />} />
                </Route>
                <Route path="/customers">
                  <Route index element={<CustomerList />} />
                  <Route path="show/:id" element={<CustomerShow />} />
                  <Route path="edit/:id" element={<CustomerEdit />} />
                </Route>
                <Route path="/customer-orders">
                  <Route index element={<CustomerOrderList />} />
                  <Route path="show/:id" element={<CustomerOrderShow />} />
                </Route>
                <Route path="/consent-logs">
                  <Route index element={<ConsentLogList />} />
                  <Route path="show/:id" element={<ConsentLogShow />} />
                </Route>
                <Route path="/recovery-events">
                  <Route index element={<RecoveryEventList />} />
                  <Route path="show/:id" element={<RecoveryEventShow />} />
                </Route>
                <Route path="*" element={<ErrorComponent />} />
              </Route>
            </Routes>
          </Refine>
        </AntdApp>
      </ConfigProvider>
    </BrowserRouter>
  );
}
